<template>
    <v-container class="max-height my-5">
        <v-row class="align-center justify-space-between">
            <h1 class="text-left">Asset Expenses</h1>
            <v-btn :loading="downloading" prepend-icon="mdi-download" size="small" variant="tonal"
                   @click="exportCsv">Export CSV</v-btn>
        </v-row>

        <v-row>
            <v-col cols="12" sm="4">
                <company-select v-if="user.type === 'admin'" v-model="formData['company']" :items="companiesList"
                          density="compact" label="Select Company"
                          @update:model-value="()=>{fetchAssetList();fetchReport()}"/>

                <v-select v-model="formData['assets']" :items="assetsList" chips clearable
                          density="compact" item-title="label" item-value="id" label="Select Asset(s)" multiple
                          @update:model-value="fetchReport()"/>

                <v-date-input
                    v-model="formData['start_date']"
                    append-inner-icon="$calendar"
                    clearable
                    density="compact"
                    hide-actions
                    label="Start Date"
                    prepend-icon=""
                    @click:clear="()=>{formData['start_date']=null;fetchReport()}"
                    @update:model-value="fetchReport()"/>

                <v-date-input
                    v-model="formData['end_date']"
                    append-inner-icon="$calendar"
                    clearable
                    density="compact"
                    hide-actions
                    label="End Date"
                    prepend-icon=""
                    @click:clear="()=>{formData['end_date']=null;fetchReport()}"
                    @update:model-value="fetchReport()"/>

                <v-select v-model="formData['rows']" :items="[10,20,50,75,100]"
                          density="compact" label="No. of Assets per page"
                          @update:model-value="fetchReport()"/>
            </v-col>

            <v-col class="d-flex justify-center" cols="12" sm="8" style="height:300px">
                <Doughnut :data="chartData" :options="chartOptions"/>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-table>
                    <thead>
                    <tr>
                        <th>Asset</th>
                        <th>Cost</th>
                        <th>Tax</th>
                        <th>Total Cost</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="item in reportData" v-if="reportData.length" class="text-left">
                        <td>{{ item.asset_name }}</td>
                        <td>$ {{ item.cost }}</td>
                        <td>$ {{ item.tax }}</td>
                        <td>$ {{ item.total_cost }}</td>
                    </tr>
                    <tr v-else>
                        <td v-if="loading" class="text-center" colspan="4">
                            <v-progress-circular color="primary" indeterminate/>
                        </td>
                        <td v-else colspan="4">No record found</td>
                    </tr>
                    </tbody>
                </v-table>
            </v-col>
        </v-row>

        <v-row v-if="totalPages > 1" justify="center">
            <v-col cols="8">
                <v-container class="max-width">
                    <v-pagination
                        v-model="currentPage"
                        :length="totalPages"
                        class="my-4"
                        density="compact"
                        @update:model-value="fetchReport"
                    ></v-pagination>
                </v-container>
            </v-col>
        </v-row>
    </v-container>
</template>

<script setup>
import {computed, onMounted, reactive, ref} from "vue";
import {send} from "@/composables/apiClient";
import {Chart, registerables} from 'chart.js';
import {useAuthStore} from "@/stores/authStore";
import {VDateInput} from "vuetify/lib/labs/VDateInput";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {useReportExport} from "@/composables/useReportExport";
import {Doughnut} from "vue-chartjs";
import CompanySelect from "@/components/globals/company-select.vue";

Chart.register(...registerables);

const {user} = useAuthStore();
const {downloadReport} = useReportExport();
const downloading = ref(false);
const reportData = ref([]);
const loading = ref(false);
const totalPages = ref(1);
const currentPage = ref(1);
const assetsList = ref([]);
const companiesList = ref([]);
const {showSnackbar} = useSnackbarStore();
const formData = reactive({
    company: ref(user.type !== 'admin' ? user.company.id : null),
    start_date: null,
    end_date: null,
    assets: null,
    rows: 10,
});

const chartOptions = {
    cutout: '45%', // default ~50%, increase for a thinner ring
    responsive: true,
    maintainAspectRatio: false, // allow resizing
    plugins: {
        legend: {
            position: 'right',
            labels: {
                boxWidth: 16,
                padding: 10
            }
        },
        tooltip: {
            callbacks: {
                label: (ctx) => {
                    const value = ctx.parsed;
                    const total = ctx.chart._metasets[0].total;
                    const percent = ((value / total) * 100).toFixed(1);
                    return ` $${value.toFixed(2)} (${percent}%)`;
                }
            }
        }
    }
};

const chartData = computed(() => {
    if (!reportData.value.length) {
        return {
            labels: [],
            datasets: [{
                data: [],
                backgroundColor: reportData.value.map(() => `hsl(${Math.random() * 360}, 70%, 60%)`),
            }]
        };
    }
    const labels = reportData.value.map(item => item.asset_name)
    const values = reportData.value.map(item => item.total_cost)

    return {
        labels,

        datasets: [{
            data: values,
            backgroundColor: reportData.value.map(() => `hsl(${Math.random() * 360}, 70%, 60%)`),
        }]
    }
})

onMounted(() => {
    if (user.type === 'admin') {
        fetchCompaniesList();
    } else {
        fetchReport();
        fetchAssetList();
    }
})

const fetchCompaniesList = () => {
    send('get', `companies/list`)
        .then((response) => {
            companiesList.value = response.data.data;
        })
        .catch((error) => {
            showSnackbar('Companies List could not be loaded', 'error')
        })
}
const fetchAssetList = () => {
    return send('get', `assets/list`, {company: formData['company']})
        .then((response) => {
            assetsList.value = response.data.data;
        })
        .catch((error) => {
            showSnackbar('Assets List could not be loaded', 'error')
        })
}
const fetchReport = () => {
    loading.value = true;
    send('get', `fleet/reports/assets-expense-report`, {...formData, page: currentPage.value})
        .then((response) => {
            reportData.value = response.data.data.data;
            totalPages.value = response.data.data.last_page;
            currentPage.value = response.data.data.current_page;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Expense report could not be loaded', 'error')
        }).finally(() => loading.value = false)
}

const exportCsv = async () => {
    if (user.type === 'admin' && !formData['company']) {
        showSnackbar('Company is required', 'error')
        return
    }
    downloading.value = true
    try {
        await downloadReport('fleet/reports/assets-expense-report', formData, 'csv', 'asset-expenses.csv')
    } catch (e) {
        showSnackbar('Export failed', 'error')
    } finally {
        downloading.value = false
    }
}
</script>
